import React from 'react';

// Isometric 3D pallet + 2D front-elevation + top-down arrangement diagram SVG.
// config: { unitsPerLayer, numberOfLayers, gridCols, unitDimL, unitDimW, unitDimH, dimUnit,
//           palletL, palletW, palletH, totalWeight, weightUnit, surfaceUsage, overhangW, overhangL,
//           mixedZone: { cols, rows, units } | null }
export default function PalletDiagram({ config, mode = '3d' }) {
  if (!config) return null;

  const {
    unitsPerLayer = 6, numberOfLayers = 6,
    gridCols: _gridCols,
    unitDimL, unitDimW,
    unitDimH = 12.6, dimUnit = 'in',
    palletL = 48, palletW = 40, palletH = 6,
    totalWeight, weightUnit = 'lbs', surfaceUsage,
    mixedZone: _mixedZone,
  } = config;

  // unitsPerLayer is the COMBINED total (main grid + mixedZone, if any) — subtract the mixed
  // zone's units back out before deriving the main grid's own row count.
  const mixedZone = _mixedZone && Number(_mixedZone.units) > 0 ? _mixedZone : null;
  const mainUnitsPerLayer = mixedZone
    ? Math.max((Number(unitsPerLayer) || 0) - mixedZone.units, 0)
    : (Number(unitsPerLayer) || 6);

  const cols = Number(_gridCols) || Math.ceil(Math.sqrt(mainUnitsPerLayer || 6));
  const rows = Math.ceil((mainUnitsPerLayer || 6) / cols);

  // ── Top-down bird's-eye view ───────────────────────────────────────────────
  if (mode === 'top') {
    const EW = 260, EH = 200, EP = 24;
    const pL = Number(palletL) || 48;
    const pW = Number(palletW) || 40;
    const scaleX = (EW - 2 * EP) / pL;
    const scaleY = (EH - 2 * EP) / pW;
    const scale  = Math.min(scaleX, scaleY);

    const px0 = (EW - pL * scale) / 2;
    const py0 = (EH - pW * scale) / 2;
    const pxW = pL * scale;
    const pyH = pW * scale;

    // Box pixel dimensions — use real dims if available, else auto-fit to pallet
    const uL = Number(unitDimL) > 0 ? Number(unitDimL) * scale : pxW / cols;
    const uW = Number(unitDimW) > 0 ? Number(unitDimW) * scale : pyH / rows;

    const units = mainUnitsPerLayer || 6;
    const boxes = [];
    let n = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (n >= units) break;
        boxes.push({ c, r });
        n++;
      }
    }

    // Mixed zone — boxes rotated 90° (L/W swapped), placed in the leftover strip immediately
    // to the right of the main grid. Real-dimension pixel sizes only (this zone only ever
    // exists when unitDimL/W were entered — see autoArrange in TechSheetPanel.js).
    const mixedBoxes = [];
    if (mixedZone && Number(unitDimL) > 0 && Number(unitDimW) > 0) {
      const mUL = Number(unitDimW) * scale; // rotated: width now runs along pallet length
      const mUW = Number(unitDimL) * scale; // rotated: length now runs along pallet width
      const mx0 = px0 + cols * uL;
      let mn = 0;
      for (let r = 0; r < mixedZone.rows; r++) {
        for (let c = 0; c < mixedZone.cols; c++) {
          if (mn >= mixedZone.units) break;
          mixedBoxes.push({ c, r, mUL, mUW, mx0 });
          mn++;
        }
      }
    }

    return (
      <svg viewBox={`0 0 ${EW} ${EH}`} style={{ width: '100%', maxWidth: EW, display: 'block' }} aria-label="Pallet top-down view">
        {/* Pallet surface */}
        <rect x={px0} y={py0} width={pxW} height={pyH} fill="#f5edd8" stroke="#8B6020" strokeWidth="1.5" rx="1" />

        {/* Boxes */}
        {boxes.map(({ c, r }, i) => {
          const bx = px0 + c * uL;
          const by = py0 + r * uW;
          const overhangs = bx + uL > px0 + pxW + 0.5 || by + uW > py0 + pyH + 0.5;
          return (
            <rect
              key={i}
              x={bx + 0.8}
              y={by + 0.8}
              width={Math.max(uL - 1.6, 1)}
              height={Math.max(uW - 1.6, 1)}
              fill={overhangs ? '#fca5a5' : '#D4A96A'}
              stroke={overhangs ? '#dc2626' : '#9A6D38'}
              strokeWidth="0.8"
              rx="0.5"
            />
          );
        })}

        {/* Mixed-zone boxes — rotated 90°, distinct fill so the orientation change reads clearly */}
        {mixedBoxes.map(({ c, r, mUL, mUW, mx0 }, i) => {
          const bx = mx0 + c * mUL;
          const by = py0 + r * mUW;
          const overhangs = bx + mUL > px0 + pxW + 0.5 || by + mUW > py0 + pyH + 0.5;
          return (
            <rect
              key={`m-${i}`}
              x={bx + 0.8}
              y={by + 0.8}
              width={Math.max(mUL - 1.6, 1)}
              height={Math.max(mUW - 1.6, 1)}
              fill={overhangs ? '#fca5a5' : '#B7D4A9'}
              stroke={overhangs ? '#dc2626' : '#5E8A4A'}
              strokeWidth="0.8"
              rx="0.5"
            />
          );
        })}

        {/* Dimension labels */}
        <text x={EW / 2} y={py0 - 6} textAnchor="middle" fontSize="8" fill="#666">
          {palletL}{dimUnit}
        </text>
        <text
          x={px0 - 8} y={EH / 2}
          textAnchor="middle" fontSize="8" fill="#666"
          transform={`rotate(-90,${px0 - 8},${EH / 2})`}
        >
          {palletW}{dimUnit}
        </text>
      </svg>
    );
  }

  // ── Isometric projection, scaled to real box dimensions ────────────────
  // Real-world unit size (falls back to a modest cube if dims aren't entered)
  const realL = Number(unitDimL) > 0 ? Number(unitDimL) : 10;
  const realW = Number(unitDimW) > 0 ? Number(unitDimW) : 10;
  const realH = Number(unitDimH) > 0 ? Number(unitDimH) : 10;

  const mainL = cols * realL;                // main-grid load footprint length
  const mainW = rows * realW;                // main-grid load footprint width
  const totalH = numberOfLayers * realH;     // full stack height

  // Mixed zone — boxes rotated 90° (L/W swapped), sitting in the leftover strip beyond the
  // main grid along the length axis. Only its top + front faces are drawn: its near-x edge
  // butts directly against the main prism (internal seam, not an exposed wall).
  const mixedL = mixedZone ? mixedZone.cols * realW : 0;
  const mixedW = mixedZone ? mixedZone.rows * realL : 0;
  const combinedL = mainL + mixedL;           // full load footprint, both zones
  const combinedW = Math.max(mainW, mixedW);

  const PAD = 14;
  const PH = 10; // pallet slab thickness, px
  const FLOOR_RATIO = 0.5; // classic 2:1 dimetric — vertical:horizontal for floor axes

  // Auto-scale (px per real unit) so the whole diagram fits a fixed budget,
  // same principle as the top-down view — long boxes render long, not square.
  const TARGET_W = 190;
  const TARGET_H = 190;
  const floorSpan = combinedL + combinedW || 1;
  const isoScaleX = TARGET_W / floorSpan;
  const isoScaleYFloor = TARGET_H / (floorSpan * FLOOR_RATIO / 2 + totalH || 1);
  const s = Math.min(isoScaleX, isoScaleYFloor, 8);

  const stepX = s;
  const stepY = s * FLOOR_RATIO / 2;
  const stepZ = s;

  const oy = floorSpan * stepY + totalH * stepZ + PAD;
  const ox = combinedW * stepX + PAD;

  function iso(wx, wy, wz) {
    return [
      ox + (wx - wy) * stepX,
      oy - (wx + wy) * stepY - wz * stepZ,
    ];
  }

  const viewW = (combinedL + combinedW) * stepX + 2 * PAD;
  const viewH = oy + PH + PAD;

  function pts(arr) {
    return arr.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ');
  }

  const EDGE = 'rgba(0,0,0,0.35)';
  const SEAM = 'rgba(0,0,0,0.16)';

  // ── Load faces ─────────────────────────────────────────────────────────
  // Treat the whole stack as one solid prism (not one polygon per box) —
  // reads as a real stack of cases instead of a busy lattice of diamonds.
  // Case boundaries are drawn as faint seam lines on top, not full outlines.
  // (0,0) is the near corner (closest to viewer) in this projection — the two
  // visible side walls are the x=0 and y=0 planes, meeting at that near edge.
  const loadTop = [
    iso(0, 0, totalH), iso(mainL, 0, totalH), iso(mainL, mainW, totalH), iso(0, mainW, totalH),
  ];
  const loadRight = [
    iso(0, 0, 0), iso(0, mainW, 0), iso(0, mainW, totalH), iso(0, 0, totalH),
  ];
  const loadFront = [
    iso(0, 0, 0), iso(mainL, 0, 0), iso(mainL, 0, totalH), iso(0, 0, totalH),
  ];

  // Faint seam lines marking individual case boundaries
  const topSeams = [];
  for (let c = 1; c < cols; c++) topSeams.push([iso(c * realL, 0, totalH), iso(c * realL, mainW, totalH)]);
  for (let r = 1; r < rows; r++) topSeams.push([iso(0, r * realW, totalH), iso(mainL, r * realW, totalH)]);

  const rightSeams = [];
  for (let r = 1; r < rows; r++) rightSeams.push([iso(0, r * realW, 0), iso(0, r * realW, totalH)]);
  for (let l = 1; l < numberOfLayers; l++) rightSeams.push([iso(0, 0, l * realH), iso(0, mainW, l * realH)]);

  const frontSeams = [];
  for (let c = 1; c < cols; c++) frontSeams.push([iso(c * realL, 0, 0), iso(c * realL, 0, totalH)]);
  for (let l = 1; l < numberOfLayers; l++) frontSeams.push([iso(0, 0, l * realH), iso(mainL, 0, l * realH)]);

  // ── Mixed-zone prism (top + front faces only — see note above) ─────────
  const mixedTop = mixedZone ? [
    iso(mainL, 0, totalH), iso(mainL + mixedL, 0, totalH), iso(mainL + mixedL, mixedW, totalH), iso(mainL, mixedW, totalH),
  ] : null;
  const mixedFront = mixedZone ? [
    iso(mainL, 0, 0), iso(mainL + mixedL, 0, 0), iso(mainL + mixedL, 0, totalH), iso(mainL, 0, totalH),
  ] : null;
  const mixedTopSeams = [];
  const mixedFrontSeams = [];
  if (mixedZone) {
    for (let c = 1; c < mixedZone.cols; c++) {
      const wx = mainL + c * realW;
      mixedTopSeams.push([iso(wx, 0, totalH), iso(wx, mixedW, totalH)]);
      mixedFrontSeams.push([iso(wx, 0, 0), iso(wx, 0, totalH)]);
    }
    for (let r = 1; r < mixedZone.rows; r++) {
      const wy = r * realL;
      mixedTopSeams.push([iso(mainL, wy, totalH), iso(mainL + mixedL, wy, totalH)]);
    }
    for (let l = 1; l < numberOfLayers; l++) {
      mixedFrontSeams.push([iso(mainL, 0, l * realH), iso(mainL + mixedL, 0, l * realH)]);
    }
  }

  // ── Pallet slab — spans the combined footprint so it visually sits under both zones ──
  function palletPt(wx, wy, offsetY = 0) {
    const [px, py] = iso(wx, wy, 0);
    return [px, py + offsetY];
  }
  const palletTop = [
    palletPt(0, 0), palletPt(combinedL, 0), palletPt(combinedL, combinedW), palletPt(0, combinedW),
  ];
  const palletRight = [
    palletPt(0, 0), palletPt(0, combinedW),
    palletPt(0, combinedW, PH), palletPt(0, 0, PH),
  ];
  const palletFront = [
    palletPt(0, 0), palletPt(combinedL, 0),
    palletPt(combinedL, 0, PH), palletPt(0, 0, PH),
  ];

  // ── 3D view ────────────────────────────────────────────────────────────
  if (mode === '3d') {
    return (
      <svg
        viewBox={`0 0 ${viewW.toFixed(0)} ${viewH.toFixed(0)}`}
        style={{ width: '100%', maxWidth: viewW, display: 'block', overflow: 'visible' }}
        aria-label="Pallet load 3D view"
      >
        <defs>
          <linearGradient id="pd-top" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#E9C68C" />
            <stop offset="100%" stopColor="#D4A96A" />
          </linearGradient>
          <linearGradient id="pd-right" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C4914F" />
            <stop offset="100%" stopColor="#A9793C" />
          </linearGradient>
          <linearGradient id="pd-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A87F45" />
            <stop offset="100%" stopColor="#8C6631" />
          </linearGradient>
          <linearGradient id="pd-pallet-top" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D4A662" />
            <stop offset="100%" stopColor="#B9803A" />
          </linearGradient>
          <linearGradient id="pd-mixed-top" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#CFE4C5" />
            <stop offset="100%" stopColor="#B7D4A9" />
          </linearGradient>
          <linearGradient id="pd-mixed-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8FAF80" />
            <stop offset="100%" stopColor="#729265" />
          </linearGradient>
        </defs>

        <g>
          {/* Pallet */}
          <polygon points={pts(palletFront)} fill="#6B4A1A" stroke={EDGE} strokeWidth="0.6" />
          <polygon points={pts(palletRight)} fill="#8B6020" stroke={EDGE} strokeWidth="0.6" />
          <polygon points={pts(palletTop)}  fill="url(#pd-pallet-top)" stroke={EDGE} strokeWidth="0.6" />

          {/* Load — one solid prism per face, not one polygon per box */}
          <polygon points={pts(loadFront)} fill="url(#pd-front)" stroke={EDGE} strokeWidth="0.8" strokeLinejoin="round" />
          <polygon points={pts(loadRight)} fill="url(#pd-right)" stroke={EDGE} strokeWidth="0.8" strokeLinejoin="round" />
          <polygon points={pts(loadTop)}   fill="url(#pd-top)"   stroke={EDGE} strokeWidth="0.8" strokeLinejoin="round" />

          {/* Faint seams marking individual case boundaries */}
          {topSeams.map(([[x1, y1], [x2, y2]], i) => (
            <line key={`ts-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={SEAM} strokeWidth="0.5" />
          ))}
          {rightSeams.map(([[x1, y1], [x2, y2]], i) => (
            <line key={`rs-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={SEAM} strokeWidth="0.5" />
          ))}
          {frontSeams.map(([[x1, y1], [x2, y2]], i) => (
            <line key={`fs-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={SEAM} strokeWidth="0.5" />
          ))}

          {/* Mixed zone — boxes rotated 90°, distinct color so the orientation change reads
              clearly. Adjacent to the main prism along its far length edge (an internal seam,
              not an exposed wall — see note above where mixedTop/mixedFront are computed). */}
          {mixedZone && (
            <>
              <polygon points={pts(mixedFront)} fill="url(#pd-mixed-front)" stroke={EDGE} strokeWidth="0.8" strokeLinejoin="round" />
              <polygon points={pts(mixedTop)}   fill="url(#pd-mixed-top)"   stroke={EDGE} strokeWidth="0.8" strokeLinejoin="round" />
              {mixedTopSeams.map(([[x1, y1], [x2, y2]], i) => (
                <line key={`mts-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={SEAM} strokeWidth="0.5" />
              ))}
              {mixedFrontSeams.map(([[x1, y1], [x2, y2]], i) => (
                <line key={`mfs-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={SEAM} strokeWidth="0.5" />
              ))}
            </>
          )}
        </g>
      </svg>
    );
  }

  // ── 2D front-elevation view ────────────────────────────────────────────
  // Simple rectangle: width = palletL, height = totalLoadH (stacked boxes + pallet)
  const EW = 180, EH = 160, EP = 20;
  const aspectL = palletL || 48;
  const aspectH = (numberOfLayers * unitDimH + palletH) || 82;
  const scaleX = (EW - 2 * EP) / aspectL;
  const scaleY = (EH - 2 * EP) / aspectH;
  const scale = Math.min(scaleX, scaleY);
  const rW = aspectL * scale;
  const rH = aspectH * scale;
  const rx = (EW - rW) / 2;
  const ry = EH - EP - rH;

  // Pallet slab at bottom
  const palH = palletH * scale;

  return (
    <svg viewBox={`0 0 ${EW} ${EH}`} style={{ width: '100%', maxWidth: EW, display: 'block' }} aria-label="Pallet load 2D view">
      {/* Box stack */}
      <rect x={rx} y={ry} width={rW} height={rH - palH} fill="#D4A96A" stroke="#9A6D38" strokeWidth="1" />
      {/* Internal horizontal lines per layer */}
      {Array.from({ length: numberOfLayers - 1 }).map((_, i) => {
        const yLine = ry + (rH - palH) * (1 - (i + 1) / numberOfLayers);
        return <line key={i} x1={rx} y1={yLine} x2={rx + rW} y2={yLine} stroke="#9A6D38" strokeWidth="0.5" />;
      })}
      {/* Pallet slab */}
      <rect x={rx} y={ry + rH - palH} width={rW} height={palH} fill="#C4933A" stroke="#8B6020" strokeWidth="1" />

      {/* Dimension labels */}
      <text x={EW / 2} y={ry - 5} textAnchor="middle" fontSize="9" fill="#555">
        {palletL}{dimUnit} × {palletW}{dimUnit}
      </text>
      <text x={rx + rW + 4} y={ry + (rH - palH) / 2} dominantBaseline="middle" fontSize="8" fill="#555">
        {(numberOfLayers * unitDimH + palletH).toFixed(1)}{dimUnit}
      </text>
      {totalWeight && (
        <text x={EW / 2} y={ry + rH + 12} textAnchor="middle" fontSize="8" fill="#555">
          {totalWeight} {weightUnit} total
        </text>
      )}
      {surfaceUsage && (
        <text x={EW / 2} y={ry + rH + 22} textAnchor="middle" fontSize="8" fill="#555">
          {surfaceUsage}% surface usage
        </text>
      )}
    </svg>
  );
}
